import { z } from "zod";

export const UserRoleEnum = z.enum(["ADMIN", "USER"]);
export type UserRole = z.infer<typeof UserRoleEnum>;

export const OrgRoleEnum = z.enum(["OWNER", "ADMIN", "MEMBER", "VIEWER"]);
export type OrgRole = z.infer<typeof OrgRoleEnum>;

export const AccountStatusEnum = z.enum([
  "CREATED",
  "UNVERIFIED",
  "ONBOARDING",
  "ACTIVE",
  "SUSPENDED",
  "LOCKED",
  "ARCHIVED",
]);
export type AccountStatus = z.infer<typeof AccountStatusEnum>;

// Schema for user search/filter params
export const UserFilterSchema = z.object({
  page: z.number().default(1),
  limit: z.number().default(10),
  orderBy: z
    .enum(["firstName", "lastName", "email", "role", "createdAt"])
    .default("createdAt"),
  orderDirection: z.enum(["asc", "desc"]).default("desc"),
  search: z.string().optional(),
  role: OrgRoleEnum.optional(), // Use organization roles
  status: AccountStatusEnum.optional(),
});
export type UserFilterInput = z.infer<typeof UserFilterSchema>;

// Schema for user in lists
export const UserListItemSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  role: z.string(), // Virtual field - can be org role or legacy role
  organizationRole: OrgRoleEnum.optional(), // Actual org role
  accountStatus: AccountStatusEnum,
  mfaEnabled: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  lastLoginAt: z.date().nullable(),
  onboardingEmailSentAt: z.date().nullable().optional(),
});
export type UserListItem = z.infer<typeof UserListItemSchema>;

// Schema for detailed user
export const UserDetailSchema = UserListItemSchema.extend({
  mfaType: z.string().nullable(),
  pendingEmail: z.string().nullable().optional(),
  onboardingEmailSentAt: z.date().nullable().optional(), // New field
});
export type UserDetail = z.infer<typeof UserDetailSchema>;

// Schema for user list response with pagination
export const PaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  totalCount: z.number(),
  totalPages: z.number(),
  hasMore: z.boolean(),
});
export type Pagination = z.infer<typeof PaginationSchema>;

export const UserListResponseSchema = z.object({
  users: z.array(UserListItemSchema),
  pagination: PaginationSchema,
});
export type UserListResponse = z.infer<typeof UserListResponseSchema>;

// Create User Schema
export const CreateUserSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email format"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  mfaEnabled: z.boolean().default(false),
  mfaType: z.string().optional(),
  orgRole: OrgRoleEnum.default("MEMBER"), // Organization role only
});
export type CreateUserInput = z.infer<typeof CreateUserSchema>;

// Update User Schema
export const UpdateUserSchema = z.object({
  id: z.string(),
  firstName: z.string().min(1, "First name is required").optional(),
  lastName: z.string().min(1, "Last name is required").optional(),
  email: z.string().email("Invalid email format").optional(),
  orgRole: z.enum(["ADMIN", "MEMBER", "VIEWER"]).optional(), // Cannot change to OWNER
  accountStatus: AccountStatusEnum.optional(),
  mfaEnabled: z.boolean().optional(),
  forcePasswordChange: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

// Reset User Password Schema
export const ResetUserPasswordSchema = z.object({
  id: z.string(),
  password: z.string().min(8, "Password must be at least 8 characters"),
});
export type ResetUserPasswordInput = z.infer<typeof ResetUserPasswordSchema>;
